import {
  Button,
  Card,
  Col,
  Flex,
  Input,
  Row,
  Select,
  Table,
  Typography,
  Avatar,
  Tag
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { userRoles } from "../../../../../../constants";
import Actions from "../../../../../../redux/actions";
import Spinner from "../../../../../common/spinner/Spinner";
import { getCustomersAction } from "../../../../../../redux/services/customers/customersServices";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { colors } from "../../../../../../constants/colors";
import { SearchOutlined } from "@ant-design/icons";
import useDebounce from "../../../../../../hooks/useDebounce";
import { getCountryAction } from "../../../../../../redux/services/auth/authServices";
import { UserOutlined } from "@ant-design/icons";
import styles from "./CustomerInfo.module.scss";
import {
  baseRouteMaker,
  baseRouteMakerForSuperAdmin,
  routes,
} from "../../../../../../constants/routeConsts";
import { dateObjToDispViewDate,getStatusNameInArabicAsWell,getUserStatus } from "../../../../../../utils/utils";

const CustomerInfo = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { customers } = useSelector((state) => state.customers);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: customers?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const { countryList } = useSelector((state) => state?.auth);
  const [selectedCountryObj, setSelectedCountryObj] = useState(null);
  /* filter dependencies */

  const selectListApiCalls = () => {
    // Country select list
    dispatch(getCountryAction());
  };

  /* on Country changed */
  const onCountryChanged = (value, element) => {
    const selectedCountryObject = countryList?.find(
      (item) => item._id === value
    );
    setSelectedCountryObj(selectedCountryObject);
  };
  /* on Country changed */

  /* function definition to get table data */
  const handleGetTableData = () => {
    dispatch(
      Actions.createAction(Actions.GET_CUSTOMERS_LIST, { customers: {} })
    );
    setLoading(true);

    const selCountryIdArr = new Array(selectedCountryObj?._id);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      country: selCountryIdArr,
      role: userRoles.CUSTOMER,
    };
    dispatch(getCustomersAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue, selectedCountryObj]);

  /* initial dependencies rendering */
  useEffect(() => {
    selectListApiCalls(null);
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.customer.customerId"),
        dataIndex: "id_number",
        key: "id_number",
      },
      {
        title: t("Modules.customer.customerName"),
        render: (_,record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                shape="large"
                src={record?.profile_image}
                key={record?.profile_image}
                icon={<UserOutlined />}
              />
               {
                record?.first_name
            ? `${record?.first_name} ${record?.last_name}`
            : "-"
               }
            </Flex>
          );
        },
      },
      {
        title: t("staffList.email"),
        dataIndex: "email",
        key: "email",
      },
      {
        title: t("staffList.phone"),
        dataIndex: "phone",
        key: "phone",
        render: (phone) => `+${phone?.country_code}-${phone?.phone_number}`,
      },
      {
        title: t("ProfileSetup.countryLabel"),
        dataIndex: "country",
        key: "country",
        render: (country) => country ? country?.emoji+" "+country?.name : "-",
      },
      {
        title: t("Modules.Provider.dateofJoining"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("Modules.AutoCredit.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getUserStatus(record?.status),
                  textTransform: "capitalize",
                }}
              >
                {getStatusNameInArabicAsWell(record?.status)}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("Modules.customer.customerInfo"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_CUSTOMER_ID, {
        selectedCustomerId: "",
      })
    );
    navigate(baseRouteMakerForSuperAdmin(routes.CUSTOMER_INFO_DETAILS_DETAILS));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedCustomerId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_CUSTOMER_ID, {
          selectedCustomerId,
        })
      );
    }
  };

  return (
    <div className="serviceProviderCustomersInfoContainer">
      <Flex vertical gap="2rem">
        <Row gutter={24} justify="space-between">
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByNameEmail")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "370px" }}
            />
          </Col>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Select
              style={{ width: "370px" }}
              placeholder={t("ProfileSetup.countryPlaceholder")}
              onChange={(value, element) => onCountryChanged(value, element)}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {countryList &&
                countryList?.map((item) => (
                  <Select.Option key={item?._id} value={item?._id}>
                    {item?.name}
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col span={8}>
            <Card
              bordered
              hoverable
              style={{ width: 240, padding: 0, borderRadius: 20 ,   border: `1px solid ${colors.oceanBlue}`}}
            >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={8}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<UserOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={16}>
                  <Flex vertical>
                    <Typography.Title level={4}>
                      {t("Modules.customer.customerCount")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.electricBlue }}
                    >
                      {customers?.total || 0}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={customers?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default CustomerInfo;
